import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { UntypedFormControl } from '@angular/forms';
import { IContentList } from '../../core/models/i-content-list';
import { IContentListFilterValues } from './i-content-list-filter-values';
import { CmsPaginationService } from '../../shared/services/cms-pagination.service';
import { UserSettingsService } from '../../shared/services/user-settings.service';
import { CmsDateTimeService } from 'src/app/shared/services/cms-date-time.service';

import * as moment from 'moment';
import { Subscription, forkJoin, of } from 'rxjs';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { ContentListService } from '../shared/services/cmsapi/content-list.service';
import { DeliveryTargetsService } from '../shared/services/cmsapi/delivery-targets.service';

import { ContentListFilterService } from '../shared/services/cmsapi/content-list-filters.service';
import { WithProcessing } from 'src/app/shared/mixins/with-processing';
import { switchMap, take } from 'rxjs/operators';
import { CitywireInternalAccountsService } from '@tpapa/citywire-internal-accounts-angular';

class ContentListsComponentBase {
  constructor() {}
}

const ContentListsComponentMixinBase = WithProcessing(
  ContentListsComponentBase
);

@Component({
  selector: 'app-content-lists',
  templateUrl: './content-lists.component.html',
})
export class ContentListsComponent
  extends ContentListsComponentMixinBase
  implements OnInit, OnDestroy
{
  constructor(
    private contentListService: ContentListService,
    private accountsService: CitywireInternalAccountsService,
    private getDeliveryTargetsService: DeliveryTargetsService,
    private userSettingsService: UserSettingsService,
    public cmsPaginationService: CmsPaginationService,
    private dialogService: DialogService,
    public contentListFilterService: ContentListFilterService
  ) {
    super();
  }

  searchContentListvalue = '';
  subscriptions: Subscription[] = [];
  dataSource = new MatTableDataSource<IContentList>();
  selectedPageSize = '10';
  websiteList = [];
  contentListTypeList: string[] = [];
  displayedColumns: string[] = [
    'name',
    'website',
    'contentListType',
    'lastModifiedDateTime',
  ];
  pagingOptions: string[] = ['10', '25', '50', '100'];
  modifiedTimeSelectionList = [
    { id: 1, name: 'Last hour' },
    { id: 2, name: 'Last 24 hours' },
    { id: 3, name: 'Last week' },
    { id: 4, name: 'Last month' },
    { id: 5, name: 'Any time' },
  ];

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  websitesFilter = new UntypedFormControl();
  contentListTypeFilter = new UntypedFormControl();
  modifiedFilter = new UntypedFormControl(this.modifiedTimeSelectionList[4].id);
  pageSizeFilter = new UntypedFormControl();
  filteredValues: IContentListFilterValues =
    this.contentListFilterService.getFilters();
  userDefaultWebsite = [];

  ngOnInit(): void {
    // TODO: Clean up init function
    this.contentListTypeList = this.contentListService.getTypes$();

    this.populateFiltersFromLocalStorage();
    this.loadContentLists();

    const getAllDeliveryTargets$ = this.getDeliveryTargetsService.getAll$();
    const getUserSettings$ = this.accountsService.currentUser.pipe(
      take(1),
      switchMap((currentUser) => {
        return this.userSettingsService.getByUserName$(
          currentUser.username.toLowerCase()
        );
      })
    );

    const deliveryTargetSubscription = forkJoin({
      allDeliveryTargets: getAllDeliveryTargets$,
      settings: getUserSettings$,
    }).subscribe(({ allDeliveryTargets, settings }) => {
      this.websiteList = allDeliveryTargets
        .filter((website) => website.isTierOneSite)
        .map((x) => x.friendlyName);

      const defaultWebsites = allDeliveryTargets
        .filter((x) => (settings.defaultDeliveryTargetCodes ?? []).includes(x.code))
        .sort((a, b) => a.friendlyName.localeCompare(b.friendlyName));

      defaultWebsites.map((x) => this.userDefaultWebsite.push(x.friendlyName));

      const hasNoWebsiteFilter = this.websitesFilter.value.length === 0;
      const hasNoContentListTypeFilter =
        this.contentListTypeFilter.value.length === 1 &&
        this.contentListTypeFilter.value.includes('');
      const hasDefaultModifiedFilter =
        this.modifiedFilter.value === this.modifiedTimeSelectionList[4].id;

      if (
        hasNoWebsiteFilter &&
        hasNoContentListTypeFilter &&
        hasDefaultModifiedFilter &&
        this.filteredValues.name === ''
      ) {
        this.websitesFilter.setValue(this.userDefaultWebsite);
      }
    });

    this.subscriptions.push(deliveryTargetSubscription);

    const websitesFilterSubscription =
      this.websitesFilter.valueChanges.subscribe((value) => {
        this.filteredValues.website = value.toString();
        this.setCurrentDataSourceFilter();
      });

    const contentListTypeFilterSubscription =
      this.contentListTypeFilter.valueChanges.subscribe((value) => {
        this.filteredValues.contentListType = value.toString() || '';
        this.setCurrentDataSourceFilter();
      });

    const modifiedFilterSubscription =
      this.modifiedFilter.valueChanges.subscribe((value) => {
        this.filteredValues.time = value;
        this.setCurrentDataSourceFilter();
      });

    const pageSizeFilterSubscription =
      this.pageSizeFilter.valueChanges.subscribe((value) => {
        this.cmsPaginationService.selectPageSize(this.dataSource, value);
      });

    this.subscriptions.push(
      deliveryTargetSubscription,
      websitesFilterSubscription,
      contentListTypeFilterSubscription,
      modifiedFilterSubscription,
      pageSizeFilterSubscription
    );
  }

  applyFilter(filterValue: string): void {
    this.filteredValues.name = filterValue.trim().toLowerCase();
    this.setCurrentDataSourceFilter();
  }

  resetFilters(): void {
    this.contentListFilterService.defaultFilters();
    this.filteredValues = this.contentListFilterService.getFilters();
    this.websitesFilter.setValue(this.userDefaultWebsite);

    this.populateFiltersFromLocalStorage();
  }

  hasFiltersSet(): boolean {
    const hasWebsiteFilter =
      JSON.stringify(this.websitesFilter.value) !==
      JSON.stringify(this.userDefaultWebsite);
    const hasContentTypeFilter =
      this.contentListTypeFilter.value.length !== 0 &&
      this.contentListTypeFilter.value[0] !== '';
    const hasModifiedFilter =
      this.modifiedFilter.value !== this.modifiedTimeSelectionList[4].id;
    const hasSearchFilter = this.searchContentListvalue !== '';

    return (
      hasWebsiteFilter ||
      hasContentTypeFilter ||
      hasModifiedFilter ||
      hasSearchFilter
    );
  }

  loadContentLists(): void {
    const getAllContentsSubscription = this.process$(
      this.contentListService.getAll$()
    ).subscribe(
      (data) => {
        this.dataSource.paginator = this.paginator;
        this.dataSource.data = data.results;
        this.dataSource.sort = this.sort;
        this.dataSource.filterPredicate = this.customFilterPredicate();
        this.dataSource.filter = JSON.stringify(this.filteredValues);
        this.cmsPaginationService.populatePageButtonArray(this.dataSource);

        this.pageSizeFilter.setValue(this.selectedPageSize);
      },
      (error) => {
        this.dialogService.openErrorDialog({
          message:
            'An error has occurred whilst loading the content lists. Please contact IT.',
        });
      }
    );
    this.subscriptions.push(getAllContentsSubscription);
  }

  private customFilterPredicate(): (
    data: IContentList,
    filter: string
  ) => boolean {
    return (data: IContentList, filter: string): boolean => {
      const searchString = JSON.parse(filter);
      const nameFound = data.name
        .toLowerCase()
        .includes(searchString.name.toLowerCase());

      const searchWebsiteArray =
        searchString.website.length === 0
          ? []
          : searchString.website.split(',');
      const websiteFound =
        searchWebsiteArray.length === 0
          ? true
          : searchWebsiteArray.some((v) => data.website.includes(v));

      const searchTypeArray =
        searchString.contentListType.length === 0
          ? []
          : searchString.contentListType.split(',');
      const typeFound =
        searchTypeArray.length === 0
          ? true
          : searchTypeArray.some((v) =>
              data.contentListType.toLowerCase().includes(v.toLowerCase())
            );

      let timeFound = true;

      const today = moment.utc().toDate();
      const dateToCheck = new Date(data.lastModifiedDateTime);

      switch (searchString.time) {
        // Last hour
        case 1: {
          timeFound =
            today.getTime() - dateToCheck.getTime() <=
            CmsDateTimeService.getHoursInMilliseconds(1);
          break;
        }
        // Last 24 hours
        case 2: {
          timeFound =
            today.getTime() - dateToCheck.getTime() <=
            CmsDateTimeService.getHoursInMilliseconds(24);
          break;
        }
        // Last week
        case 3: {
          timeFound =
            today.getTime() - dateToCheck.getTime() <=
            CmsDateTimeService.getDaysInMilliseconds(7);
          break;
        }
        // Last month
        case 4: {
          timeFound =
            today.getTime() - dateToCheck.getTime() <=
            CmsDateTimeService.getDaysInMilliseconds(31);
          break;
        }
        // Any time
        case 5:
        default: {
          timeFound = true;
          break;
        }
      }

      return nameFound && websiteFound && typeFound && timeFound;
    };
  }

  private setCurrentDataSourceFilter(): void {
    this.dataSource.filter = JSON.stringify(this.filteredValues);

    this.contentListFilterService.setFilters(this.filteredValues);

    this.cmsPaginationService.setPageToDefault(this.dataSource);
  }

  private populateFiltersFromLocalStorage(): void {
    this.searchContentListvalue = this.filteredValues.name;

    if (this.filteredValues.website !== '') {
      this.websitesFilter.patchValue(this.filteredValues.website.split(','));
    } else {
      this.websitesFilter.patchValue([]);
    }

    this.contentListTypeFilter.patchValue(
      this.filteredValues.contentListType.split(',')
    );
    this.modifiedFilter.patchValue(this.filteredValues.time);

    this.setCurrentDataSourceFilter();
  }

  getQueryParams(
    websiteFilter: string[]
  ): { website: string } | { website?: undefined } {
    return websiteFilter && websiteFilter.length >= 1
      ? { website: websiteFilter.sort()[0] }
      : {};
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
}
